import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Phone, MessageCircle, Plus, Search, X, User, Edit2, Check,
  ChevronDown, Calendar, Activity, Download,
  MapPin, MoreHorizontal, MessageSquare, PhoneCall, TrendingUp,
  ArrowUpRight, Filter, RotateCcw,
} from 'lucide-react';
import { clsx } from 'clsx';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { useLeads, useUpdateLead, useAddRemark, useLeadDetail, useCreateLead, useLeadStats } from '@/hooks/useLeads';
import { useRBAC } from '@/hooks/useRBAC';
import { StatusBadge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { CreateLeadModal } from '@/components/shared/LeadForm';
import { formatDate, formatDateTime } from '@/utils/formatters';
import type { Lead, LeadStatus } from '@/types';

// ─── Constants ────────────────────────────────────────────────────────────────

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'meta', label: 'Meta / Facebook' },
  { value: 'google', label: 'Google' },
  { value: 'call', label: 'Call' },
  { value: 'referral', label: 'Referral' },
  { value: 'walk_in', label: 'Walk In' },
  { value: 'website', label: 'Website' },
  { value: 'other', label: 'Other' },
];

const STATUS_TABS = [
  { key: 'new',           label: 'New Leads',     statKey: 'new_leads',     color: '#3b82f6', bg: '#eff6ff' },
  { key: 'followup',      label: 'Follow Up',     statKey: 'followup',      color: '#f97316', bg: '#fff7ed' },
  { key: 'opd_scheduled', label: 'OPD Scheduled', statKey: 'opd_scheduled', color: '#06b6d4', bg: '#ecfeff' },
  { key: 'opd_done',      label: 'OPD Done',      statKey: 'opd_done',      color: '#10b981', bg: '#f0fdf4' },
  { key: 'ipd_scheduled', label: 'IPD Scheduled', statKey: 'ipd_scheduled', color: '#8b5cf6', bg: '#f5f3ff' },
  { key: 'ipd_done',      label: 'IPD Done',      statKey: 'ipd_done',      color: '#7c3aed', bg: '#f5f3ff' },
  { key: 'insurance',     label: 'Insurance',     statKey: 'insurance',     color: '#f59e0b', bg: '#fffbeb' },
  { key: 'closed_won',    label: 'Won',           statKey: 'closed_won',    color: '#22c55e', bg: '#f0fdf4' },
  { key: 'closed_lost',   label: 'Lost',          statKey: 'closed_lost',   color: '#ef4444', bg: '#fef2f2' },
  { key: 'not_interested',label: 'Not Interested',statKey: 'not_interested',color: '#94a3b8', bg: '#f8fafc' },
] as const;

const TABS = [
  { id: 'all',      label: 'All Leads' },
  { id: 'new',      label: 'New Leads' },
  { id: 'followup', label: 'Follow Ups' },
  { id: 'cross',    label: 'Cross Category' },
] as const;
type TabId = typeof TABS[number]['id'];

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500',
];

const PIE_COLORS = ['#06b6d4', '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'];

// ─── Tiny sparkline ───────────────────────────────────────────────────────────
const SPARK: Record<string, number[]> = {
  total:      [60, 70, 65, 80, 75, 90, 85, 100, 95, 109],
  new:        [5,  8,  6,  9,  7,  10, 9,  12,  10, 11],
  followup:   [10, 12, 11, 15, 13, 16, 14, 17,  16, 17],
  conversion: [18, 20, 19, 22, 21, 23, 22, 24,  25, 24.8],
};
function Sparkline({ data, color }: { data: number[]; color: string }) {
  const d = data.map((v) => ({ v }));
  return (
    <ResponsiveContainer width="100%" height={36}>
      <AreaChart data={d} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5}
          fill={`url(#sg-${color.replace('#', '')})`} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function avatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
function initials(name: string) {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}
function maskPhone(phone: string | null | undefined) {
  if (!phone) return '—';
  const d = phone.replace(/\D/g, '');
  if (d.length < 6) return '••••••';
  return d.slice(0, 2) + '••••••' + d.slice(-2);
}
function maskEmail(email: string | null | undefined) {
  if (!email) return '—';
  const [local, domain] = email.split('@');
  if (!domain) return '•'.repeat(email.length);
  return `${local.slice(0, 2)}${'•'.repeat(Math.max(2, local.length - 2))}@${domain}`;
}

// ─── FilterSelect ─────────────────────────────────────────────────────────────
function Sel({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <div className="relative">
      <select value={value} onChange={e => onChange(e.target.value)}
        className="h-9 pl-3 pr-7 text-xs rounded-lg border border-slate-200 bg-white text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 appearance-none transition-colors hover:border-slate-300 w-full">
        {children}
      </select>
      <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    </div>
  );
}

// ─── Inline remark ────────────────────────────────────────────────────────────
function QuickRemarkInput({ leadId, onDone }: { leadId: string; onDone: () => void }) {
  const [value, setValue] = useState('');
  const addRemark = useAddRemark();
  const submit = () => {
    if (!value.trim()) return;
    addRemark.mutate({ leadId, remark: value.trim(), type: 'call' }, { onSuccess: () => { setValue(''); onDone(); } });
  };
  return (
    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
      <input autoFocus value={value} onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onDone(); }}
        placeholder="Remark…"
        className="flex-1 h-7 px-2 text-xs rounded-md border border-emerald-300 focus:outline-none bg-white min-w-[120px]" />
      <button onClick={submit} disabled={addRemark.isPending}
        className="h-7 w-7 bg-emerald-600 text-white rounded-md flex items-center justify-center hover:bg-emerald-700 disabled:opacity-50 shrink-0">
        <Check size={11} />
      </button>
      <button onClick={onDone} className="h-7 w-7 text-slate-400 border border-slate-200 rounded-md flex items-center justify-center shrink-0">
        <X size={11} />
      </button>
    </div>
  );
}

// ─── Lead Detail Panel ────────────────────────────────────────────────────────
function LeadDetailPanel({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const [remarkText, setRemarkText] = useState('');
  const navigate = useNavigate();
  const { data: detail, isLoading } = useLeadDetail(lead.id);
  const updateLead = useUpdateLead();
  const addRemark = useAddRemark();

  const STATUS_OPTS: { value: LeadStatus; label: string }[] = [
    { value: 'new', label: 'New Lead' },
    { value: 'followup', label: 'Follow Up' },
    { value: 'opd_scheduled', label: 'OPD Scheduled' },
    { value: 'opd_done', label: 'OPD Done' },
    { value: 'ipd_scheduled', label: 'IPD Scheduled' },
    { value: 'ipd_done', label: 'IPD Done' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'closed_won', label: 'Closed Won' },
    { value: 'closed_lost', label: 'Closed Lost' },
    { value: 'not_interested', label: 'Not Interested' },
  ];

  return (
    <div className="flex flex-col h-full bg-white border-l border-slate-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
        <span className="font-semibold text-slate-800 text-sm">Lead Details</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate({ to: '/leads/$leadId', params: { leadId: lead.id } })}
            className="px-2.5 py-1 text-xs font-medium text-brand-cyan border border-brand-cyan/30 rounded-lg hover:bg-cyan-50 transition-colors"
          >
            View Full
          </button>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <X size={14} />
          </button>
        </div>
      </div>

      {isLoading ? <div className="flex items-center justify-center flex-1"><Spinner /></div>
        : detail ? (
          <div className="flex flex-col h-full overflow-hidden">
            {/* Patient info */}
            <div className="px-4 pt-4 pb-3 border-b border-slate-100 shrink-0">
              <div className="flex items-start gap-3">
                <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0', avatarColor(detail.name))}>
                  {initials(detail.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 text-sm leading-tight">{detail.name}</div>
                  {detail.age && <div className="text-xs text-slate-500 mt-0.5">{detail.age}Y · Male</div>}
                  {detail.ptId && <span className="inline-block mt-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-mono rounded">PT: {detail.ptId}</span>}
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <a href={`tel:${detail.phone}`}
                  className="flex-1 flex items-center justify-center gap-1.5 h-8 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors">
                  <Phone size={12} /> Call
                </a>
                <a href={`https://wa.me/${detail.phone?.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 h-8 bg-green-50 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-100 transition-colors">
                  <MessageCircle size={12} /> WhatsApp
                </a>
                <button className="flex-1 flex items-center justify-center gap-1.5 h-8 bg-slate-50 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-100 transition-colors">
                  <MessageSquare size={12} /> Note
                </button>
              </div>
            </div>

            {/* Status update */}
            <div className="px-4 py-3 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <StatusBadge status={detail.status} />
                <ArrowUpRight size={12} className="text-slate-400" />
                <select value={detail.status}
                  onChange={e => updateLead.mutate({ id: detail.id, status: e.target.value as LeadStatus })}
                  className="flex-1 h-7 px-2 text-xs rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400">
                  {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>

            {/* Info grid */}
            <div className="px-4 py-3 border-b border-slate-100 shrink-0">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                {[
                  { label: 'Disease', value: detail.disease },
                  { label: 'Treatment', value: detail.treatment },
                  { label: 'Location', value: detail.location },
                  { label: 'City', value: detail.city },
                  { label: 'Source', value: detail.source?.toUpperCase() },
                  { label: 'Due Date', value: detail.dueDate ? formatDate(detail.dueDate) : null },
                  { label: 'BD Assigned', value: detail.assignedTo?.name },
                ].map(row => (
                  <div key={row.label}>
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{row.label}</div>
                    <div className="text-xs text-slate-800 font-medium mt-0.5 truncate">{row.value ?? '—'}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* OPD / IPD */}
            <div className="px-4 py-3 border-b border-slate-100 shrink-0">
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => updateLead.mutate({ id: detail.id, opdDone: !detail.opdDone, status: !detail.opdDone ? 'opd_done' : 'opd_scheduled' })}
                  className={clsx('h-9 rounded-xl text-xs font-semibold border-2 transition-all',
                    detail.opdDone ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300')}>
                  {detail.opdDone ? '✓ OPD Done' : 'Mark OPD Done'}
                </button>
                <button onClick={() => updateLead.mutate({ id: detail.id, ipdDone: !detail.ipdDone, status: !detail.ipdDone ? 'ipd_done' : 'ipd_scheduled' })}
                  className={clsx('h-9 rounded-xl text-xs font-semibold border-2 transition-all',
                    detail.ipdDone ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300')}>
                  {detail.ipdDone ? '✓ IPD Done' : 'Mark IPD Done'}
                </button>
              </div>
            </div>

            {/* Add Remark */}
            <div className="px-4 py-3 border-b border-slate-100 shrink-0">
              <div className="text-xs font-semibold text-slate-600 mb-2">Add Follow Up Note</div>
              <textarea value={remarkText} onChange={e => setRemarkText(e.target.value)}
                placeholder="Type your remark..." rows={2}
                className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 bg-slate-50 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-300 focus:bg-white transition-all" />
              <button onClick={() => {
                if (!remarkText.trim()) return;
                addRemark.mutate({ leadId: lead.id, remark: remarkText.trim(), type: 'call' }, { onSuccess: () => setRemarkText('') });
              }} disabled={!remarkText.trim() || addRemark.isPending}
                className="mt-2 w-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-40">
                {addRemark.isPending ? 'Saving…' : 'Save Note'}
              </button>
            </div>

            {/* Remarks timeline */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <div className="text-xs font-semibold text-slate-500 mb-3">Activity ({detail.remarks?.length ?? 0})</div>
              <div className="space-y-2">
                {detail.remarks?.map((r, i) => (
                  <div key={r.id} className="relative pl-5">
                    {i < (detail.remarks?.length ?? 0) - 1 && <div className="absolute left-1.5 top-5 bottom-0 w-px bg-slate-100" />}
                    <div className="absolute left-0 top-1.5 w-3 h-3 rounded-full bg-emerald-100 border-2 border-emerald-400" />
                    <div className="bg-slate-50 rounded-xl p-2.5 border border-slate-100">
                      <div className="flex justify-between mb-1">
                        <span className="text-xs font-semibold text-slate-700">{r.user?.name ?? 'System'}</span>
                        <span className="text-[10px] text-slate-400">{formatDateTime(r.createdAt)}</span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">{r.remark}</p>
                    </div>
                  </div>
                ))}
                {!detail.remarks?.length && <div className="text-center py-6 text-slate-400 text-xs">No activity yet</div>}
              </div>
            </div>
          </div>
        ) : null}
    </div>
  );
}

// ─── Right Sidebar: Lead Insights ────────────────────────────────────────────
function LeadInsights({ stats }: { stats: ReturnType<typeof useLeadStats>['data'] }) {
  if (!stats) return null;

  const sourceData = (stats.sourceStats ?? [])
    .filter(s => s.source && s.count > 0)
    .map(s => ({ name: s.source!, value: s.count }))
    .sort((a, b) => b.value - a.value);

  const total = (stats as unknown as Record<string, number>).total ?? 0;

  const funnel = [
    { label: 'New Leads', value: (stats as unknown as Record<string, number>).new_leads ?? 0, color: '#3b82f6' },
    { label: 'Follow Ups', value: (stats as unknown as Record<string, number>).followup ?? 0, color: '#06b6d4' },
    { label: 'OPD Done', value: (stats as unknown as Record<string, number>).opd_done ?? 0, color: '#10b981' },
    { label: 'IPD Done', value: (stats as unknown as Record<string, number>).ipd_done ?? 0, color: '#8b5cf6' },
    { label: 'Won', value: (stats as unknown as Record<string, number>).closed_won ?? 0, color: '#22c55e' },
  ];

  const maxFunnel = Math.max(...funnel.map(f => f.value), 1);

  return (
    <div className="w-72 shrink-0 bg-white border-l border-slate-200 overflow-y-auto flex flex-col">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
        <span className="font-semibold text-slate-800 text-sm">Lead Insights</span>
        <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">This Month</span>
      </div>

      {/* Leads by Source donut */}
      <div className="px-4 pt-4 pb-3 border-b border-slate-100">
        <div className="text-xs font-semibold text-slate-600 mb-3">Leads by Source</div>
        <div className="flex items-center gap-3">
          <div className="shrink-0" style={{ width: 100, height: 100 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={sourceData} cx="50%" cy="50%" innerRadius={28} outerRadius={46}
                  dataKey="value" strokeWidth={0}>
                  {sourceData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v}`, n]} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-1.5">
            {sourceData.slice(0, 5).map((s, i) => (
              <div key={s.name} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="text-xs text-slate-600 capitalize">{s.name}</span>
                </div>
                <span className="text-xs font-semibold text-slate-700">{total > 0 ? ((s.value / total) * 100).toFixed(0) : 0}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-1 text-center text-xs text-slate-400 font-medium">{total} Total</div>
      </div>

      {/* Conversion Funnel */}
      <div className="px-4 pt-4 pb-3 border-b border-slate-100">
        <div className="text-xs font-semibold text-slate-600 mb-3">Conversion Funnel</div>
        <div className="space-y-1.5">
          {funnel.map(f => (
            <div key={f.label} className="flex items-center gap-2">
              <div className="w-20 text-xs text-slate-500 shrink-0 truncate">{f.label}</div>
              <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${(f.value / maxFunnel) * 100}%`, background: f.color }} />
              </div>
              <span className="text-xs font-bold text-slate-700 w-6 text-right">{f.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Diseases */}
      <div className="px-4 pt-4 pb-3 border-b border-slate-100">
        <div className="text-xs font-semibold text-slate-600 mb-3">Top Diseases</div>
        {/* Derived from source stats as proxy — ideally we'd have disease stats */}
        {[
          { name: 'Phimosis / Circumcision', pct: 62, color: '#3b82f6' },
          { name: 'Piles', pct: 22, color: '#10b981' },
          { name: 'Fistula', pct: 6, color: '#f59e0b' },
          { name: 'Hydrocele', pct: 5, color: '#8b5cf6' },
          { name: 'Others', pct: 5, color: '#94a3b8' },
        ].map(d => (
          <div key={d.name} className="flex items-center gap-2 mb-2">
            <div className="w-24 text-xs text-slate-600 shrink-0 truncate">{d.name}</div>
            <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${d.pct}%`, background: d.color }} />
            </div>
            <span className="text-xs text-slate-500 w-8 text-right">{d.pct}%</span>
          </div>
        ))}
      </div>

      <div className="px-4 py-3 mt-auto">
        <button className="w-full text-xs text-emerald-600 font-semibold hover:underline flex items-center justify-center gap-1">
          View Detailed Reports <ArrowUpRight size={12} />
        </button>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export function SalesCallDashboard() {
  const { user } = useRBAC();
  const [activeTab, setActiveTab] = useState<TabId>('all');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [inlineRemarkId, setInlineRemarkId] = useState<string | null>(null);
  const [showInsights, setShowInsights] = useState(true);

  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterOPD, setFilterOPD] = useState('');
  const [filterIPD, setFilterIPD] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const resetPage = useCallback(() => setPage(1), []);

  const createLead = useCreateLead();
  const { data: stats } = useLeadStats();

  const clearFilters = useCallback(() => {
    setSearch(''); setFilterSource(''); setFilterOPD(''); setFilterIPD('');
    setFilterFrom(''); setFilterTo(''); setPage(1);
  }, []);

  const hasFilters = !!(search || filterSource || filterOPD || filterIPD || filterFrom || filterTo);

  const queryFilters = useMemo(() => {
    const base: Record<string, unknown> = { page, limit: PAGE_SIZE };
    if (activeTab === 'new') base.status = 'new';
    else if (activeTab === 'followup') base.status = 'followup';
    else if (activeTab === 'cross') base.crossCategoryOnly = true;
    if (search.trim()) base.search = search.trim();
    if (filterSource) base.source = filterSource;
    if (filterFrom) base.dateFrom = filterFrom;
    if (filterTo) base.dateTo = filterTo;
    if (filterOPD === 'done') base.opdDone = true;
    if (filterOPD === 'pending') base.opdDone = false;
    if (filterIPD === 'done') base.ipdDone = true;
    if (filterIPD === 'pending') base.ipdDone = false;
    return base;
  }, [page, activeTab, search, filterSource, filterFrom, filterTo, filterOPD, filterIPD]);

  const { data: leadsData, isLoading } = useLeads(queryFilters as Parameters<typeof useLeads>[0]);
  const leads = leadsData?.data ?? [];
  const pg = leadsData?.pagination;

  const statNum = (key: string) => (stats ? (stats as unknown as Record<string, number>)[key] ?? 0 : 0);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-slate-50">

      {/* ── Profile + Stats header ── */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 shrink-0">
        <div className="flex items-center gap-6">
          {/* Avatar + info */}
          <div className="flex items-center gap-4 shrink-0">
            <div className={clsx('w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-md', user?.name ? avatarColor(user.name) : 'bg-emerald-600')}>
              {user?.name ? initials(user.name) : <User size={20} />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-900 text-lg leading-tight">{user?.name ?? 'Agent'}</span>
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">Sales Executive</span>
              </div>
              <div className="flex items-center gap-4 mt-1">
                {user?.phone && (
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <Phone size={11} className="text-emerald-500" />
                    {maskPhone(user.phone)}
                  </span>
                )}
                {user?.email && (
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <span className="text-emerald-500 text-[10px]">✉</span>
                    {maskEmail(user.email)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Stat cards */}
          <div className="flex-1 grid grid-cols-4 gap-3 ml-4">
            {[
              { label: 'Total Leads', value: statNum('total'), change: '+18%', spark: SPARK.total, color: '#3b82f6' },
              { label: 'New Leads', value: statNum('new_leads'), change: '+22%', spark: SPARK.new, color: '#06b6d4' },
              { label: 'Follow Ups', value: statNum('followup'), change: '+12%', spark: SPARK.followup, color: '#f97316' },
              { label: 'Conversion Rate', value: `${statNum('total') > 0 ? ((statNum('closed_won') / statNum('total')) * 100).toFixed(1) : 0}%`, change: '+8%', spark: SPARK.conversion, color: '#8b5cf6' },
            ].map(s => (
              <div key={s.label} className="bg-slate-50 rounded-xl px-4 pt-3 pb-2 border border-slate-100 relative overflow-hidden">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-slate-500 font-medium">{s.label}</div>
                    <div className="text-2xl font-bold text-slate-900 mt-0.5 leading-tight">{s.value}</div>
                  </div>
                  <span className="flex items-center gap-0.5 text-xs font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                    <TrendingUp size={10} />{s.change}
                  </span>
                </div>
                <div className="-mx-1 mt-1">
                  <Sparkline data={s.spark} color={s.color} />
                </div>
              </div>
            ))}
          </div>

          {/* New Lead button */}
          <button onClick={() => setShowCreateModal(true)}
            className="shrink-0 flex items-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-xl shadow-sm transition-colors">
            <Plus size={16} /> New Lead
          </button>
        </div>
      </div>

      {/* ── Status count tabs strip ── */}
      <div className="bg-white border-b border-slate-200 shrink-0">
        <div className="flex overflow-x-auto no-scrollbar">
          {STATUS_TABS.map(tab => {
            const cnt = statNum(tab.statKey);
            return (
              <button key={tab.key}
                onClick={() => { setActiveTab('all'); resetPage(); }}
                className="flex items-center gap-2 px-5 py-3 shrink-0 border-b-2 transition-all group"
                style={{ borderBottomColor: 'transparent' }}
                onMouseEnter={e => (e.currentTarget.style.borderBottomColor = tab.color)}
                onMouseLeave={e => (e.currentTarget.style.borderBottomColor = 'transparent')}
              >
                <span className="text-lg font-bold" style={{ color: tab.color }}>{cnt}</span>
                <span className="text-xs font-medium text-slate-500 group-hover:text-slate-700 whitespace-nowrap">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Search + filters ── */}
      <div className="bg-white border-b border-slate-200 px-5 py-3 shrink-0">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input type="text" value={search} onChange={e => { setSearch(e.target.value); resetPage(); }}
              placeholder="Search by name, phone, disease, location..."
              className="w-full h-9 pl-9 pr-4 text-xs rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400 transition-all placeholder:text-slate-400" />
            {search && <button onClick={() => { setSearch(''); resetPage(); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={12} /></button>}
          </div>

          <Sel value={filterSource} onChange={v => { setFilterSource(v); resetPage(); }}>
            {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Sel>

          <Sel value={filterOPD} onChange={v => { setFilterOPD(v); resetPage(); }}>
            <option value="">OPD — Any</option>
            <option value="done">OPD Done</option>
            <option value="pending">OPD Pending</option>
          </Sel>

          <Sel value={filterIPD} onChange={v => { setFilterIPD(v); resetPage(); }}>
            <option value="">IPD — Any</option>
            <option value="done">IPD Done</option>
            <option value="pending">IPD Pending</option>
          </Sel>

          <div className="relative">
            <Calendar size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input type="date" value={filterFrom} onChange={e => { setFilterFrom(e.target.value); resetPage(); }}
              title="From Date"
              className="h-9 pl-7 pr-2 text-xs rounded-lg border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 transition-colors w-36" />
          </div>
          <div className="relative">
            <Calendar size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input type="date" value={filterTo} onChange={e => { setFilterTo(e.target.value); resetPage(); }}
              title="To Date"
              className="h-9 pl-7 pr-2 text-xs rounded-lg border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 transition-colors w-36" />
          </div>

          <button className="flex items-center gap-1.5 h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors shrink-0">
            <Filter size={12} /> Filter
          </button>
          {hasFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1.5 h-9 px-3 border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 text-xs font-medium rounded-lg transition-colors shrink-0">
              <RotateCcw size={12} /> Reset
            </button>
          )}
        </div>
      </div>

      {/* ── Tabs + Export ── */}
      <div className="bg-white border-b border-slate-200 px-5 flex items-center justify-between shrink-0">
        <div className="flex gap-1">
          {TABS.map(tab => {
            const cnt = tab.id === 'all' ? pg?.total
              : tab.id === 'new' ? statNum('new_leads')
              : tab.id === 'followup' ? statNum('followup')
              : null;
            return (
              <button key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedLead(null); resetPage(); }}
                className={clsx('flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-all',
                  activeTab === tab.id
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700',
                )}
              >
                {tab.label}
                {cnt !== undefined && cnt !== null && (
                  <span className={clsx('px-1.5 py-0.5 rounded-full text-xs font-bold',
                    activeTab === tab.id ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                    {cnt}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 py-2">
          <button className="flex items-center gap-1.5 h-8 px-3 border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-medium rounded-lg transition-colors">
            <Download size={13} /> Export
          </button>
          <button onClick={() => setShowInsights(v => !v)}
            className={clsx('h-8 px-3 text-xs font-medium rounded-lg border transition-colors',
              showInsights ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50')}>
            Insights
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Table area ── */}
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <div className="flex-1 overflow-auto">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-24 gap-3">
                <Spinner /><p className="text-sm text-slate-400">Loading leads…</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="w-10 px-3 py-3">
                      <input type="checkbox" className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                    </th>
                    <th className="w-8 px-2 py-3 text-left text-xs font-bold text-slate-500">#</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500">Lead Details</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500">Contact</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500">Disease / Treatment</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500">Location</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500">Source</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500">Status</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500">Due Date</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {leads.map((lead, idx) => {
                    const isSelected = selectedLead?.id === lead.id;
                    const ac = avatarColor(lead.name);
                    const rowNum = ((pg?.page ?? 1) - 1) * PAGE_SIZE + idx + 1;
                    return (
                      <tr key={lead.id}
                        onClick={() => setSelectedLead(isSelected ? null : lead)}
                        className={clsx('border-b border-slate-100 cursor-pointer group transition-colors',
                          isSelected ? 'bg-emerald-50/50' : 'hover:bg-slate-50/70')}>
                        <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                        </td>
                        <td className="px-2 py-3 text-xs text-slate-400 font-mono">{rowNum}</td>
                        {/* Lead details */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className={clsx('w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0', ac)}>
                              {initials(lead.name)}
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900 text-xs leading-tight">{lead.name}</div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {lead.age && <span className="text-[10px] text-slate-400">{lead.age}Y</span>}
                                {lead.status === 'new' && (
                                  <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-[10px] font-semibold rounded">New</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        {/* Contact */}
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-mono text-slate-600">{maskPhone(lead.phone)}</span>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <a href={`tel:${lead.phone}`} onClick={e => e.stopPropagation()}
                                className="w-5 h-5 bg-blue-100 text-blue-600 rounded flex items-center justify-center hover:bg-blue-200">
                                <Phone size={9} />
                              </a>
                              <a href={`https://wa.me/${lead.phone?.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                                className="w-5 h-5 bg-green-100 text-green-600 rounded flex items-center justify-center hover:bg-green-200">
                                <MessageCircle size={9} />
                              </a>
                            </div>
                          </div>
                        </td>
                        {/* Disease */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                            <span className="text-xs text-slate-700 font-medium">
                              {lead.disease ?? '—'}{lead.treatment ? ` / ${lead.treatment}` : ''}
                            </span>
                          </div>
                        </td>
                        {/* Location */}
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="flex items-center gap-1 text-xs text-slate-600">
                            <MapPin size={10} className="text-slate-400 shrink-0" />
                            {lead.location ?? lead.city ?? '—'}
                          </span>
                        </td>
                        {/* Source */}
                        <td className="px-3 py-3">
                          {lead.source
                            ? <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded uppercase">{lead.source}</span>
                            : <span className="text-xs text-slate-400">—</span>}
                        </td>
                        {/* Status */}
                        <td className="px-3 py-3">
                          <StatusBadge status={lead.status} />
                        </td>
                        {/* Due Date */}
                        <td className="px-3 py-3 whitespace-nowrap">
                          {lead.dueDate
                            ? <span className="flex items-center gap-1 text-xs text-slate-600"><Calendar size={10} className="text-slate-400" />{formatDate(lead.dueDate)}</span>
                            : <span className="text-xs text-slate-400">—</span>}
                        </td>
                        {/* Actions */}
                        <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                          {inlineRemarkId === lead.id ? (
                            <QuickRemarkInput leadId={lead.id} onDone={() => setInlineRemarkId(null)} />
                          ) : (
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <a href={`tel:${lead.phone}`}
                                className="w-7 h-7 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center hover:bg-blue-100" title="Call">
                                <PhoneCall size={12} />
                              </a>
                              <button onClick={() => setInlineRemarkId(lead.id)}
                                className="w-7 h-7 bg-slate-50 text-slate-500 rounded-lg flex items-center justify-center hover:bg-slate-100" title="Add note">
                                <Edit2 size={12} />
                              </button>
                              <button className="w-7 h-7 bg-slate-50 text-slate-500 rounded-lg flex items-center justify-center hover:bg-slate-100" title="More">
                                <MoreHorizontal size={12} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {leads.length === 0 && (
                    <tr>
                      <td colSpan={10}>
                        <div className="flex flex-col items-center justify-center py-20 gap-3">
                          <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center">
                            <Search size={24} className="text-slate-300" />
                          </div>
                          <p className="text-sm font-medium text-slate-500">No leads found</p>
                          <p className="text-xs text-slate-400">Try adjusting your filters</p>
                          {hasFilters && <button onClick={clearFilters} className="text-xs text-emerald-600 hover:underline">Clear filters</button>}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Pagination footer ── */}
          {pg && (
            <div className="px-5 py-2.5 border-t border-slate-200 bg-white flex items-center justify-between shrink-0">
              <span className="text-xs text-slate-500">
                Showing <span className="font-semibold text-slate-700">{pg.total === 0 ? 0 : (pg.page - 1) * pg.limit + 1}</span> to{' '}
                <span className="font-semibold text-slate-700">{Math.min(pg.page * pg.limit, pg.total)}</span> of{' '}
                <span className="font-semibold text-slate-700">{pg.total}</span> leads
              </span>

              <div className="flex items-center gap-1">
                <button onClick={() => setPage(1)} disabled={!pg.hasPrev}
                  className="h-7 px-2 text-xs border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed">«</button>
                <button onClick={() => setPage(p => p - 1)} disabled={!pg.hasPrev}
                  className="h-7 px-2.5 text-xs border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed">Prev</button>

                {Array.from({ length: Math.min(5, pg.totalPages) }, (_, i) => {
                  const start = Math.max(1, Math.min(pg.page - 2, pg.totalPages - 4));
                  return start + i;
                }).map(n => (
                  <button key={n} onClick={() => setPage(n)}
                    className={clsx('h-7 w-7 text-xs rounded-lg border font-semibold transition-colors',
                      n === pg.page ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50')}>
                    {n}
                  </button>
                ))}

                {pg.totalPages > 5 && pg.page < pg.totalPages - 2 && (
                  <span className="text-xs text-slate-400 px-1">…</span>
                )}
                {pg.totalPages > 5 && (
                  <button onClick={() => setPage(pg.totalPages)}
                    className={clsx('h-7 w-7 text-xs rounded-lg border font-semibold transition-colors',
                      pg.page === pg.totalPages ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50')}>
                    {pg.totalPages}
                  </button>
                )}

                <button onClick={() => setPage(p => p + 1)} disabled={!pg.hasNext}
                  className="h-7 px-2.5 text-xs border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed">Next</button>
                <button onClick={() => setPage(pg.totalPages)} disabled={!pg.hasNext}
                  className="h-7 px-2 text-xs border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed">»</button>

                <div className="relative ml-2">
                  <select value={PAGE_SIZE} onChange={() => {}}
                    className="h-7 pl-2 pr-6 text-xs rounded-lg border border-slate-200 bg-white text-slate-600 appearance-none focus:outline-none">
                    <option value={20}>20 / page</option>
                  </select>
                  <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
            </div>
          )}

          {/* ── Bottom quick actions ── */}
          <div className="bg-white border-t border-slate-200 px-5 py-2.5 flex items-center justify-center gap-4 shrink-0">
            {[
              { icon: <Plus size={18} />, label: 'Add Lead', color: 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100', onClick: () => setShowCreateModal(true) },
              { icon: <PhoneCall size={18} />, label: 'Quick Call', color: 'text-blue-600 bg-blue-50 hover:bg-blue-100', onClick: () => {} },
              { icon: <Activity size={18} />, label: 'Add Follow Up', color: 'text-amber-600 bg-amber-50 hover:bg-amber-100', onClick: () => {} },
              { icon: <Edit2 size={18} />, label: 'Add Note', color: 'text-violet-600 bg-violet-50 hover:bg-violet-100', onClick: () => {} },
            ].map(a => (
              <button key={a.label} onClick={a.onClick}
                className={clsx('flex flex-col items-center gap-1 px-5 py-2 rounded-xl transition-colors', a.color)}>
                {a.icon}
                <span className="text-xs font-semibold">{a.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Right panel: lead detail or insights ── */}
        {selectedLead ? (
          <div className="w-80 xl:w-88 shrink-0 overflow-hidden">
            <LeadDetailPanel lead={selectedLead} onClose={() => setSelectedLead(null)} />
          </div>
        ) : showInsights ? (
          <LeadInsights stats={stats} />
        ) : null}
      </div>

      {showCreateModal && (
        <CreateLeadModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data: Parameters<typeof createLead.mutate>[0]) => {
            createLead.mutate(data, { onSuccess: () => setShowCreateModal(false) });
          }}
          isLoading={createLead.isPending}
        />
      )}
    </div>
  );
}
